import useTheme from "../../hooks/useTheme";
import { darken } from "polished";
import {
  Button as RebassButton,
  ButtonProps as ButtonPropsOriginal,
} from "rebass/styled-components";
import styled from "styled-components/macro";
import { isMobile } from "../../utils/userAgent";

import { RowBetween, RowCenter } from "../Row";
import { faCheck, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";

type ButtonProps = Omit<ButtonPropsOriginal, "css">;

export const BaseButton = styled(RebassButton) <
  {
    padding?: string;
    width?: string;
    $borderRadius?: string;
    altDisabledStyle?: boolean;
  } & ButtonProps
>`
  padding: ${({ padding }) => padding ?? "16px"};
  width: ${({ width }) => width ?? "100%"};
  font-weight: 500;
  text-align: center;
  border-radius: ${({ $borderRadius }) => $borderRadius ?? "20px"};
  outline: none;
  border: 1px solid transparent;
  color: ${({ theme }) => theme.text1};
  text-decoration: none;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 1;
  &:disabled {
    opacity: 50%;
    cursor: auto;
    pointer-events: none;
  }

  will-change: transform;
  transition: transform 450ms ease;
  transform: perspective(1px) translateZ(0);

  > * {
    user-select: none;
  }

  > a {
    text-decoration: none;
  }
`;


export const ButtonLinkMenu = styled(Link) <{ activecolor: string }>`
  text-transform: uppercase;
  font-size: 20px;
  color: ${({ theme, activecolor }) => activecolor === "true" ? theme.text3 : theme.text1};
  text-decoration: none;
  :hover {
    color: ${({ theme }) => theme.text3};
  }
  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 18px;
  `};

  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 18px;
  `};
`;

export const ButtonLink = styled(Link) <
  {
    padding?: string;
    width?: string;
    $borderRadius?: string;
    altDisabledStyle?: boolean;
  } & ButtonProps
>`
  background:  ${({ theme }) => theme.primary1};
  color: ${({ theme }) => theme.text2};
  padding: ${({ padding }) => padding ?? "16px"};
  width: ${({ width }) => width ?? "100%"};
  font-weight: 500;
  text-align: center;
  border-radius: ${({ $borderRadius }) => $borderRadius ?? "20px"};
  outline: none;
  border: 1px solid transparent;
  text-decoration: none;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 1;
  &:disabled {
    opacity: 50%;
    cursor: auto;
    pointer-events: none;
  }

  will-change: transform;
  transition: transform 450ms ease;
  transform: perspective(1px) translateZ(0);

  > * {
    user-select: none;
  }

  > a {
    text-decoration: none;
  }
  :hover {
    background: ${({ theme }) => theme.secondary3};
  }
`;

export const ButtonExternalLink = styled.a <
  {
    padding?: string;
    width?: string;
    $borderRadius?: string;
    altDisabledStyle?: boolean;
  } & ButtonProps
>`
  background:  ${({ theme }) => theme.primary1};
  color: ${({ theme }) => theme.text2};
  padding: ${({ padding }) => padding ?? "16px"};
  width: ${({ width }) => width ?? "100%"};
  font-weight: 500;
  text-align: center;
  border-radius: ${({ $borderRadius }) => $borderRadius ?? "20px"};
  outline: none;
  border: 1px solid transparent;
  text-decoration: none;
  display: flex;
  justify-content: center;
  flex-wrap: nowrap;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 1;
  &:disabled {
    opacity: 50%;
    cursor: auto;
    pointer-events: none;
  }

  will-change: transform;
  transition: transform 450ms ease;
  transform: perspective(1px) translateZ(0);

  > * {
    user-select: none;
  }

  > a {
    text-decoration: none;
  }
`;

export const ButtonPrimary = styled(BaseButton)`
  background:  ${({ theme }) => theme.primary1};
  color: ${({ theme }) => theme.text2};
`;

export const ButtonOption = styled(BaseButton) <{ active: boolean }>`
  background: ${({ theme, active }) => active ? theme.primary3 : theme.primary4};
  color: ${({ theme }) => theme.text2};
  border: 1px solid ${({ theme, active }) => active ? theme.secondary1 : "transparent"};
  border-radius: 8px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2); 
  transition: box-shadow 0.3s ease;
  &:hover {
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2); 
    background:  ${({ theme }) => theme.primary3};
    border: 1px solid  ${({ theme }) => theme.secondary1};
  }
`;

export const ButtonLight = styled(BaseButton)`
  background-color: ${({ theme }) => theme.primary3};
  color: ${({ theme }) => theme.text1};
  font-size: 16px;
  font-weight: 500;
  &:focus {
    box-shadow: 0 0 0 1pt
      ${({ theme, disabled }) => !disabled && darken(0.03, theme.primary5)};
    background-color: ${({ theme, disabled }) =>
    !disabled && darken(0.03, theme.primary5)};
  }
  &:hover {
    background-color: ${({ theme, disabled }) =>
    !disabled && darken(0.03, theme.primary5)};
  }
  &:active {
    box-shadow: 0 0 0 1pt
      ${({ theme, disabled }) => !disabled && darken(0.05, theme.primary5)};
    background-color: ${({ theme, disabled }) =>
    !disabled && darken(0.05, theme.primary5)};
  }
  :disabled {
    opacity: 0.4;
    :hover {
      cursor: auto;
      background-color: ${({ theme }) => theme.primary5};
      box-shadow: none;
      border: 1px solid transparent;
      outline: none;
    }
  }
`;

export const ButtonGray = styled(BaseButton)`
  background-color: ${({ theme }) => theme.bg1};
  color: ${({ theme }) => theme.text2};
  font-size: 16px;
  font-weight: 500;

  &:hover {
    background-color: ${({ theme, disabled }) =>
    !disabled && darken(0.05, theme.bg2)};
  }
  &:active {
    background-color: ${({ theme, disabled }) =>
    !disabled && darken(0.1, theme.bg2)};
  }
`;

export const ButtonSecondary = styled(BaseButton)`
  border: 1px solid ${({ theme }) => theme.secondary1};
  color: ${({ theme }) => theme.secondary1};
  background-color: transparent;

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => theme.secondary2};
    border: 1px solid ${({ theme }) => theme.secondary2};
  }
  &:hover {
    border: 1px solid ${({ theme }) => theme.secondary2};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => theme.secondary2};
    border: 1px solid ${({ theme }) => theme.secondary2};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
  a:hover {
    text-decoration: none;
  }
`;

export const ButtonOutlined = styled(BaseButton)`
  border: 1px solid ${({ theme }) => theme.bg2};
  background-color: transparent;
  color: ${({ theme }) => theme.text1};
  &:focus {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.bg4};
  }
  &:hover {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.bg4};
  }
  &:active {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.bg4};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`;

export const ButtonYellow = styled(BaseButton)`
  background-color: ${({ theme }) => theme.yellow3};
  color: white;
  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.05, theme.yellow3)};
    background-color: ${({ theme }) => darken(0.05, theme.yellow3)};
  }
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.yellow3)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.yellow3)};
    background-color: ${({ theme }) => darken(0.1, theme.yellow3)};
  }
  &:disabled {
    background-color: ${({ theme }) => theme.yellow3};
    opacity: 50%;
    cursor: auto;
  }
`;

export const ButtonEmpty = styled(BaseButton)`
  background-color: transparent;
  color: ${({ theme }) => theme.primary1};

  &:focus {
    text-decoration: underline;
  }
  &:hover {
    text-decoration: none;
  }
  &:active {
    text-decoration: none;
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`;

export const ButtonText = styled(BaseButton)`
  padding: 0;
  width: fit-content;
  background: none;
  text-decoration: none;
  &:focus {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    text-decoration: none;
  }
  &:hover {
    // text-decoration: underline;
    opacity: 0.9;
  }
  &:active {
    text-decoration: none;
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
  }
`;

const ButtonConfirmedStyle = styled(BaseButton)`
  background-color: ${({ theme }) => theme.bg3};
  color: ${({ theme }) => theme.text1};
  /* border: 1px solid ${({ theme }) => theme.green1}; */

  &:disabled {
    opacity: 50%;
    background-color: ${({ theme }) => theme.bg2};
    color: ${({ theme }) => theme.text2};
    cursor: auto;
  }
`;

const ButtonErrorStyle = styled(BaseButton)`
  background-color: ${({ theme }) => theme.red1};
  border: 1px solid ${({ theme }) => theme.red1};

  &:focus {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.05, theme.red1)};
    background-color: ${({ theme }) => darken(0.05, theme.red1)};
  }
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.red1)};
  }
  &:active {
    box-shadow: 0 0 0 1pt ${({ theme }) => darken(0.1, theme.red1)};
    background-color: ${({ theme }) => darken(0.1, theme.red1)};
  }
  &:disabled {
    opacity: 50%;
    cursor: auto;
    box-shadow: none;
    background-color: ${({ theme }) => theme.red1};
    border: 1px solid ${({ theme }) => theme.red1};
  }
`;

export const ButtonTap = styled(BaseButton) <{ active?: boolean }>`
  height: 10px;
  font-size: 16px;
  background-color: ${({ theme, active }) =>
    active ? theme.primary1 : theme.bg3};
`;

export function ButtonConfirmed({
  confirmed,
  altDisabledStyle,
  ...rest
}: { confirmed?: boolean; altDisabledStyle?: boolean } & ButtonProps) {
  if (confirmed) {
    return <ButtonConfirmedStyle {...rest} />;
  } else {
    return <ButtonPrimary {...rest} altDisabledStyle={altDisabledStyle} />;
  }
}

export function ButtonError({
  error,
  ...rest
}: { error?: boolean } & ButtonProps) {
  if (error) {
    return <ButtonErrorStyle {...rest} />;
  } else {
    return <ButtonPrimary {...rest} />;
  }
}

export function ButtonDropdown({
  disabled = false,
  children,
  ...rest
}: { disabled?: boolean } & ButtonProps) {
  return (
    <ButtonPrimary {...rest} disabled={disabled}>
      <RowBetween>
        <div style={{ display: "flex", alignItems: "center" }}>{children}</div>
        <FontAwesomeIcon icon={faChevronDown} width={24} />
      </RowBetween>
    </ButtonPrimary>
  );
}

export function ButtonDropdownLight({
  disabled = false,
  children,
  ...rest
}: { disabled?: boolean } & ButtonProps) {
  return (
    <ButtonOutlined {...rest} disabled={disabled}>
      <RowBetween>
        <div style={{ display: "flex", alignItems: "center" }}>{children}</div>
        <FontAwesomeIcon icon={faChevronDown} width={24} />
      </RowBetween>
    </ButtonOutlined>
  );
}

export function ButtonDropdownText({
  disabled = false,
  children,
  ...rest
}: { disabled?: boolean } & ButtonProps) {
  return (
    <ButtonText {...rest} disabled={disabled}>
      <RowCenter>
        <div style={{ display: "flex", alignItems: "center" }}>{children}</div>
        {!disabled && <FontAwesomeIcon icon={faChevronDown} width={24} />}
      </RowCenter>
    </ButtonText>
  );
}

const ActiveOutlined = styled(ButtonOutlined)`
  border: 1px solid;
  border-color: ${({ theme }) => theme.secondary1};
`;

const Circle = styled.div<{ isMobile?: boolean }>`
  height: ${({ isMobile }) => (isMobile ? "12px" : "17px")};
  width: ${({ isMobile }) => (isMobile ? "12px" : "17px")};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.secondary1};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CheckboxWrapper = styled.div`
  width: 20px;
  padding: 0 10px;
  position: absolute;
  top: 15px;
  right: 15px;
`;

export function ButtonRadioChecked({
  active = false,
  children,
  ...rest
}: { active?: boolean } & ButtonProps) {
  const theme = useTheme();

  if (!active) {
    return (
      <ButtonOutlined $borderRadius="12px" padding="12px 8px" {...rest}>
        {<RowBetween>{children}</RowBetween>}
      </ButtonOutlined>
    );
  } else {
    return (
      <ActiveOutlined {...rest} padding="12px 8px" $borderRadius="12px">
        {
          <RowBetween>
            {children}
            <CheckboxWrapper>
              <Circle>
                <FontAwesomeIcon
                  icon={faCheck}
                  width={24}
                  stroke={theme.white}
                />
              </Circle>
            </CheckboxWrapper>
          </RowBetween>
        }
      </ActiveOutlined>
    );
  }
}

export function ButtonRadioCheckedCenter({
  active = false,
  children,
  ...rest
}: { active?: boolean } & ButtonProps) {
  const theme = useTheme();

  if (!active) {
    return (
      <ButtonOutlined $borderRadius="12px" padding="12px 8px" {...rest}>
        {<RowCenter>{children}</RowCenter>}
      </ButtonOutlined>
    );
  } else {
    return (
      <ActiveOutlined {...rest} padding="12px 8px" $borderRadius="12px">
        {
          <RowCenter>
            {children}
            <CheckboxWrapper>
              <Circle isMobile={isMobile}>
                <FontAwesomeIcon
                  icon={faCheck}
                  width={24}
                  stroke={theme.white}
                />
              </Circle>
            </CheckboxWrapper>
          </RowCenter>
        }
      </ActiveOutlined>
    );
  }
}


export const ButtonChange = styled(ButtonPrimary) <{ active: boolean }>`
    background: ${({ theme, active }) => active ? theme.secondary2 : "transparent"};
    width: fit-content;
    font-size: 20px;
    
    ${({ theme }) => theme.mediaWidth.upToMedium`
        font-size: 16px;
        width: 100%;
    `};

    ${({ theme }) => theme.mediaWidth.upToSmall`
        font-size: 16px;
        width: 100%;
    `};
`;

export const ButtonSelect = styled(BaseButton) <{ active: boolean }>`
    background: ${({ theme, active }) => active ? theme.secondary3 : theme.bg1};
    color: ${({ theme }) => theme.text1};
    border: 1px solid ${({ theme }) => theme.primary5};
    font-size: 20px;
    box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.2); /* Establece la sombra para el efecto de relieve */
    transition: box-shadow 0.3s ease; /* Agrega una transición suave para el cambio de sombra */

    &:hover {
      box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2); /* Cambia la sombra al hacer hover para un efecto más pronunciado */
    }
    ${({ theme }) => theme.mediaWidth.upToMedium`
        font-size: 16px;
    `};

    ${({ theme }) => theme.mediaWidth.upToSmall`
        font-size: 16px;
    `};
`;
